/**
 * Underland fitment selector.
 *
 * The dropdowns are a remote control; Globo is the source of truth. Picking a
 * vehicle ticks Globo's own (hidden) checkboxes. Globo does the filtering, owns
 * the URL, and owns the applied state. This panel offers the cascading UI on
 * top and mirrors Globo's state back so the two cannot disagree.
 *
 * All four levels drive ONE Globo filter: Vehicle Fitment, sourced from
 * fitment.vehicles. A fitment label encodes make + model + trim + year range,
 * so Make/Model/Year/Trim are UI levels that decide WHICH labels get ticked.
 * Globo ORs values inside a filter group, so ticking every Chevrolet label is
 * "anything that fits a Chevrolet".
 *
 * This replaces an earlier design that projected five flat metafields out of
 * fitment.vehicles and gave each level its own Globo filter. That layer needed
 * a Flow, a backfill and a re-sync to stay correct, and every one of those was
 * a way for the storefront to go quietly wrong. There is now nothing to keep in
 * sync: add a vehicle to a product in admin and the filter reflects it.
 *
 * See docs/fitment-selector.md.
 */
(function () {
  'use strict';

  var config = window.UnderlandFitmentConfig || {};

  /* Heading of the one Globo block we drive, lowercased. Must match the filter
   * name configured in the Globo dashboard. */
  var FITMENT_LABEL = normaliseConfig(config.fitmentLabel || 'vehicle fitment');

  function normaliseConfig(text) {
    return String(text).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  var LEVELS = ['make', 'model', 'year', 'trim'];

  /* While we are the ones clicking Globo's checkboxes, its re-renders are our
   * own echo. Suppress state re-reading so reconcile never fights us. */
  var SELF_DRIVE_MS = 800;

  var hierarchy = null;
  /* Every spelling Globo might use for the universal record: its display name,
   * and its bare metaobject id for when Globo has not resolved the name. */
  var universalValues = [];
  var selfDriveUntil = 0;
  var panel = null;
  var observer = null;
  var observedTree = null;

  /* What the shopper has asked for. Globo stays the source of truth for what is
   * APPLIED, but a re-render can briefly stop offering a label we still need,
   * so intent is held here and converged toward on each pass rather than being
   * re-derived from a half-rendered tree and lost. */
  var desired = { make: '', model: '', year: '', trim: '' };

  /* Whether Globo has been seen holding exactly the selection above. Until it
   * has, a mismatch means the tree was not offering every label yet and we keep
   * driving toward it. Once it has, a mismatch is the shopper clearing or
   * removing something through Globo's own UI, and the tree wins. Without this
   * the panel cannot tell "not applied yet" from "just un-applied", and answers
   * a Clear all by immediately re-applying the vehicle. */
  var converged = false;

  /* Every fitment label this page has ever offered, accumulated rather than
   * replaced. Globo narrows the group as filters are applied, so the current
   * offer is not the page's inventory - taking options from it directly would
   * shrink the dropdowns as the shopper drills in and strand them. The union
   * only grows, so it stays a stable answer to "what does this page carry". */
  var seenLabels = null;

  /* ---------------------------------------------------------------- Globo DOM
   * Everything below reads or drives Globo's rendered tree. These helpers are
   * the only place that knows what Globo's markup looks like. */

  function tree() {
    return document.getElementById('gf-tree');
  }

  function normalise(text) {
    return String(text)
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\s*clear$/, ''); // Globo appends a "Clear" link inside the title
  }

  function handle() {
    return config.fitmentHandle || FITMENT_LABEL.replace(/[^a-z0-9]+/g, '-');
  }

  /**
   * The chips in the applied-filter list, which survive when Globo stops
   * rendering the option block. If a selection empties the result set Globo
   * drops the block entirely - reading or clearing through it alone would leave
   * the panel unable to see or undo what is applied.
   */
  function chips() {
    return document.querySelectorAll(
      '.globo-selected-items [data-fhandle="' + handle() + '"][data-fvalue]'
    );
  }

  function block() {
    var root = tree();
    if (!root) return null;

    var blocks = root.querySelectorAll('.gf-option-block');
    var loose = null;

    for (var i = 0; i < blocks.length; i++) {
      var titleEl = blocks[i].querySelector('.gf-block-title');
      if (!titleEl) continue;
      var title = normalise(titleEl.textContent);

      if (title === FITMENT_LABEL) return tag(blocks[i]);

      // Tolerate a heading carrying extra words - "Vehicle Fitment" for a
      // filter configured as "Vehicle". An exact match always wins.
      if (!loose && (title.indexOf(FITMENT_LABEL) === 0 || FITMENT_LABEL.indexOf(title) === 0)) {
        loose = blocks[i];
      }
    }

    return loose ? tag(loose) : null;
  }

  // Tagging the block is how the CSS hides Globo's native vehicle list, so
  // there is no hard-coded Globo filter id to keep in sync.
  function tag(node) {
    node.classList.add('underland-fitment-source');
    return node;
  }

  function buttons() {
    var found = block();
    return found ? found.querySelectorAll('button[role="checkbox"][data-fvalue]') : [];
  }

  function isUniversal(value) {
    return universalValues.indexOf(value) !== -1;
  }

  function isChecked(button) {
    if (button.getAttribute('aria-checked') === 'true') return true;
    if (button.classList.contains('gf-checked')) return true;
    var input = button.querySelector('input[type="checkbox"]');
    return !!(input && input.checked);
  }

  /** Fitment labels currently applied, ignoring the universal value. */
  function checkedLabels() {
    var values = [];
    var nodes = buttons();

    if (nodes.length) {
      for (var i = 0; i < nodes.length; i++) {
        var value = nodes[i].getAttribute('data-fvalue');
        if (!isUniversal(value) && isChecked(nodes[i])) values.push(value);
      }
      return values;
    }

    // No block rendered - read what is applied from the chips instead.
    var applied = chips();
    for (var j = 0; j < applied.length; j++) {
      var chipValue = applied[j].getAttribute('data-fvalue');
      if (!isUniversal(chipValue)) values.push(chipValue);
    }
    return values;
  }

  /**
   * The part of a selection this page can actually show. Globo only offers the
   * records its products carry, so a Colorado selection on a page holding two
   * Colorado records resolves to five labels but can only ever apply two.
   * Comparing against the full five would read as "never applied".
   */
  function reachableIn(labels, offered) {
    if (!offered) return labels;
    return labels.filter(function (label) { return offered[label]; });
  }

  /** Fold what Globo is offering right now into the page's known inventory. */
  function recordOffered(offered) {
    if (!offered) return;
    if (!seenLabels) seenLabels = {};
    for (var label in offered) {
      if (Object.prototype.hasOwnProperty.call(offered, label)) seenLabels[label] = true;
    }
  }

  function carriesSeen(fitments) {
    for (var label in fitments) {
      if (Object.prototype.hasOwnProperty.call(fitments, label) && seenLabels[label]) return true;
    }
    return false;
  }

  /**
   * Options are narrowed to what this page actually carries, and never to
   * nothing. Offering a vehicle with no products here is a dead end: the panel
   * has no label to tick, so it applies nothing and the shopper sees the whole
   * unfiltered grid, which reads as a broken filter. Falling back to the full
   * list when the intersection empties keeps an escape route out of a state
   * where every dropdown would otherwise be blank.
   */
  function available(all, carried) {
    if (!seenLabels) return all;
    var offered = all.filter(carried);
    return offered.length ? offered : all;
  }

  function makesAvailable() {
    return available(Object.keys(hierarchy.makes).sort(), function (make) {
      var models = modelsFor(make);
      for (var model in models) {
        if (Object.prototype.hasOwnProperty.call(models, model) &&
            carriesSeen(models[model].fitments)) return true;
      }
      return false;
    });
  }

  function modelsAvailable(make) {
    return available(Object.keys(modelsFor(make)).sort(), function (model) {
      return carriesSeen(fitmentsFor(make, model));
    });
  }

  function yearsAvailable(make, model) {
    return available(yearsFor(make, model), function (year) {
      return resolveFitments(make, model, year, '').some(function (label) {
        return seenLabels[label];
      });
    });
  }

  /* A trim is only worth offering when this page carries a record for that
   * trim specifically. Where it does not, selecting it resolves to the same
   * trim-less record as selecting nothing, so the option would sit there
   * changing no results - which reads as a filter that does not work. */
  function trimsAvailable(make, model, year) {
    if (!seenLabels) return trimsFor(make, model, year);
    var trims = {};
    eachFitment(make, model, function (label, fitment) {
      if (!fitment.trim || !seenLabels[label]) return;
      if (year && !coversYear(fitment, year)) return;
      trims[fitment.trim] = true;
    });
    return Object.keys(trims).sort();
  }

  /** Which fitment labels Globo currently offers. Null when nothing rendered. */
  function offeredLabels() {
    if (!tree()) return null;
    var nodes = buttons();
    if (!nodes.length) return null;
    var set = {};
    for (var i = 0; i < nodes.length; i++) set[nodes[i].getAttribute('data-fvalue')] = true;
    return set;
  }

  /**
   * Make the applied labels exactly `values`, plus the universal value when
   * Globo offers one and anything is applied - so universal-fit products keep
   * surfacing alongside the vehicle's own parts.
   *
   * Buttons are collected before any click because Globo rebuilds the tree on
   * each change; clicking the captured nodes in one synchronous pass lets its
   * debounce coalesce them into a single request.
   */
  function applyLabels(values) {
    var wanted = {};
    for (var i = 0; i < values.length; i++) wanted[values[i]] = true;
    // Tick whichever spelling of the universal record this page offers.
    if (values.length) {
      for (var u = 0; u < universalValues.length; u++) wanted[universalValues[u]] = true;
    }

    var nodes = buttons();

    // Clearing a selection whose block Globo no longer renders: remove it
    // through its chips, the only handle left on it.
    if (!nodes.length) {
      if (values.length) return;
      var applied = chips();
      if (!applied.length) return;
      selfDriveUntil = Date.now() + SELF_DRIVE_MS;
      for (var c = 0; c < applied.length; c++) applied[c].click();
      return;
    }

    var j, value;

    // Globo narrows a group's own values once something in it is ticked, so a
    // new vehicle's labels can be missing from the tree entirely. Untick
    // everything - the universal value included, or it alone keeps the group
    // collapsed - and let the re-render restore the full list. reconcile has
    // not converged, so it applies the selection on the next pass.
    if (values.length) {
      // Count only the vehicle's own labels. The universal value is offered
      // alongside every selection, so counting it would read as "reachable"
      // while the vehicle itself is nowhere in the tree.
      var reachable = 0;
      for (j = 0; j < nodes.length; j++) {
        if (values.indexOf(nodes[j].getAttribute('data-fvalue')) !== -1) reachable++;
      }
      if (!reachable) {
        var stale = [];
        for (j = 0; j < nodes.length; j++) {
          if (isChecked(nodes[j])) stale.push(nodes[j]);
        }
        if (!stale.length) return;
        selfDriveUntil = Date.now() + SELF_DRIVE_MS;
        for (var s = 0; s < stale.length; s++) stale[s].click();
        return;
      }
    }

    var toClick = [];
    for (j = 0; j < nodes.length; j++) {
      value = nodes[j].getAttribute('data-fvalue');
      if (!!wanted[value] !== isChecked(nodes[j])) toClick.push(nodes[j]);
    }

    if (!toClick.length) return;
    selfDriveUntil = Date.now() + SELF_DRIVE_MS;
    for (var k = 0; k < toClick.length; k++) toClick[k].click();
  }

  /* ------------------------------------------------------------- The cascade
   * A fitment label encodes make + model + trim + year range. The hierarchy
   * decomposes each label, which is what lets four dropdowns drive one facet. */

  function modelsFor(make) {
    var entry = hierarchy.makes[make];
    return entry ? entry.models : {};
  }

  function fitmentsFor(make, model) {
    var models = modelsFor(make);
    return models[model] ? models[model].fitments : {};
  }

  function eachFitment(make, model, fn) {
    var fitments = fitmentsFor(make, model);
    for (var label in fitments) {
      if (Object.prototype.hasOwnProperty.call(fitments, label)) fn(label, fitments[label]);
    }
  }

  function coversYear(fitment, year) {
    var value = Number(year);
    return value >= fitment.yearStart && value <= fitment.yearEnd;
  }

  /* A fitment with no trim covers every trim of that model, so it matches
   * whichever trim is asked for. Generic Colorado parts fit a Colorado ZR2. */
  function matchesTrim(fitment, trim) {
    return !trim || !fitment.trim || fitment.trim === trim;
  }

  function yearsFor(make, model) {
    var years = {};
    eachFitment(make, model, function (label, fitment) {
      for (var y = fitment.yearStart; y <= fitment.yearEnd; y++) years[y] = true;
    });
    return Object.keys(years).sort(function (a, b) { return b - a; });
  }

  function trimsFor(make, model, year) {
    var trims = {};
    eachFitment(make, model, function (label, fitment) {
      if (year && !coversYear(fitment, year)) return;
      if (fitment.trim) trims[fitment.trim] = true;
    });
    return Object.keys(trims).sort();
  }

  /** The fitment labels a selection resolves to. Make alone is a valid
   *  selection - "anything that fits a Chevrolet" - and so is make + model. */
  function resolveFitments(make, model, year, trim) {
    if (!make) return [];
    var matched = [];
    var models = model ? [model] : Object.keys(modelsFor(make));

    for (var i = 0; i < models.length; i++) {
      eachFitment(make, models[i], function (label, fitment) {
        if (year && !coversYear(fitment, year)) return;
        if (!matchesTrim(fitment, trim)) return;
        matched.push(label);
      });
    }
    return matched.sort();
  }

  /**
   * Rebuild a selection from the labels Globo has applied.
   *
   * Needed for deep links, for Globo's own Clear, and for a shopper removing a
   * single chip from the sidebar. Every candidate selection is resolved and
   * compared against what is applied, most specific first, so the answer is the
   * selection that actually produces this set rather than a guess from it.
   */
  function readSelection(labels, previous, offered) {
    var empty = { make: '', model: '', year: '', trim: '' };
    if (!labels.length) return empty;

    function produces(make, model, year, trim) {
      return sameValues(reachableIn(resolveFitments(make, model, year, trim), offered), labels);
    }

    // Several years resolve to the same records - every year inside a
    // generation's range does - so the applied set cannot say which one the
    // shopper picked. When their last selection still produces exactly this
    // set, keep it rather than replacing it with another year that filters
    // identically but is not what they chose.
    if (previous && previous.make &&
        produces(previous.make, previous.model, previous.year, previous.trim)) {
      return previous;
    }

    var makes = {};
    for (var i = 0; i < labels.length; i++) {
      var owner = ownerOf(labels[i]);
      if (!owner) return empty; // a label the hierarchy does not know
      makes[owner.make] = true;
    }

    var found = Object.keys(makes);
    if (found.length !== 1) return empty; // more than one make cannot be a cascade
    var make = found[0];

    var models = Object.keys(modelsFor(make));
    for (var m = 0; m < models.length; m++) {
      // Oldest first, so an ambiguous match reports the year the range starts
      // at - "2023" for a 2023-2026 generation reads better than "2026".
      var years = yearsFor(make, models[m]).slice().reverse();
      for (var y = 0; y < years.length; y++) {
        var trims = trimsFor(make, models[m], years[y]);
        for (var t = 0; t < trims.length; t++) {
          if (produces(make, models[m], years[y], trims[t])) {
            return { make: make, model: models[m], year: years[y], trim: trims[t] };
          }
        }
        if (produces(make, models[m], years[y], '')) {
          return { make: make, model: models[m], year: years[y], trim: '' };
        }
      }
      if (produces(make, models[m], '', '')) {
        return { make: make, model: models[m], year: '', trim: '' };
      }
    }

    if (produces(make, '', '', '')) {
      return { make: make, model: '', year: '', trim: '' };
    }

    // A partial set - most often a chip removed by hand. Keep what can still be
    // read off it so the panel shows where the shopper is.
    var seen = {};
    for (var p = 0; p < labels.length; p++) seen[ownerOf(labels[p]).model] = true;
    var single = Object.keys(seen);
    return { make: make, model: single.length === 1 ? single[0] : '', year: '', trim: '' };
  }

  var ownerIndex = null;

  /** Which make and model a fitment label belongs to. */
  function ownerOf(label) {
    if (!ownerIndex) {
      ownerIndex = {};
      for (var make in hierarchy.makes) {
        if (!Object.prototype.hasOwnProperty.call(hierarchy.makes, make)) continue;
        var models = modelsFor(make);
        for (var model in models) {
          if (!Object.prototype.hasOwnProperty.call(models, model)) continue;
          var fitments = models[model].fitments;
          for (var name in fitments) {
            if (Object.prototype.hasOwnProperty.call(fitments, name)) {
              ownerIndex[name] = { make: make, model: model };
            }
          }
        }
      }
    }
    return ownerIndex[label] || null;
  }

  /* ------------------------------------------------------------------- Panel */

  function dropdown(level) {
    return panel ? panel.querySelector('[data-fitment-level="' + level + '"]') : null;
  }

  function fill(level, values, current) {
    var select = dropdown(level);
    if (!select) return;

    var selected = values.indexOf(current) !== -1 ? current : '';

    // Rebuilding the options closes a dropdown the shopper has open, and
    // reconcile runs on every Globo re-render - so only touch the DOM when
    // something actually changed.
    var signature = values.join('|') + '>' + selected;
    if (select.underlandSignature === signature) return;
    select.underlandSignature = signature;

    var placeholder = select.getAttribute('data-placeholder') || 'Select';
    var html = '<option value="">' + escapeHtml(placeholder) + '</option>';
    for (var i = 0; i < values.length; i++) {
      html += '<option value="' + escapeAttr(values[i]) + '">' + escapeHtml(values[i]) + '</option>';
    }

    select.innerHTML = html;
    select.value = selected;
    select.disabled = values.length === 0;
  }

  function escapeHtml(value) {
    return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  /**
   * Options come from the hierarchy, never from what Globo currently offers.
   *
   * Globo narrows a group's own values once something in it is ticked, so
   * intersecting against them would collapse Make to the one make already
   * chosen and strand the shopper with no way to switch vehicle. A label the
   * tree is not offering right now simply does not get ticked; the next
   * re-render picks it up.
   */
  function render(state) {
    fill('make', makesAvailable(), state.make);
    fill('model', state.make ? modelsAvailable(state.make) : [], state.model);

    // Each level stays locked until its parent is chosen, so the cascade reads
    // top to bottom: no trim before a year, no year before a model.
    fill('year', state.make && state.model ? yearsAvailable(state.make, state.model) : [], state.year);
    fill(
      'trim',
      state.make && state.model && state.year
        ? trimsAvailable(state.make, state.model, state.year)
        : [],
      state.trim
    );

    renderChips(state);
    applyLock();
  }

  /* ------------------------------------------------------------------ Chips
   * Globo's own chips for this filter are hidden: one vehicle is many records,
   * so a Colorado selection would print five near-identical rows. These are
   * one chip per chosen LEVEL, injected into Globo's own applied-filter list so
   * a vehicle, a brand and a product type all cluster in one place.
   *
   * They deliberately carry no data-fid or data-fvalue. Globo's own handler for
   * ".globo-selected-items-wrapper .gf-option-label a" is guarded on both being
   * present, so it no-ops on ours while its CSS still styles them. */

  var CLOSE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true">' +
    '<path fill="currentColor" d="M11.414 10l6.293-6.293a1 1 0 10-1.414-1.414L10 8.586 ' +
    '3.707 2.293a1 1 0 00-1.414 1.414L8.586 10l-6.293 6.293a1 1 0 101.414 1.414L10 ' +
    '11.414l6.293 6.293A.998.998 0 0018 17a.999.999 0 00-.293-.707L11.414 10z"></path></svg>';

  /**
   * Globo renders this list only while something is applied - which is exactly
   * when we have a chip to add, so there is no case where we need it and it is
   * missing.
   *
   * Its SHAPE differs by layout, and matching only one is how this shipped
   * doing nothing: the sidebar layout emits
   * <div class="gf-block-content globo-selected-items"> holding <div> rows,
   * while the inline layout emits <ul class="globo-selected-items"> holding
   * <li> rows. Match on the class alone, and take the row element from the
   * container rather than assuming either.
   */
  function chipList() {
    var root = tree();
    if (!root) return null;
    return root.querySelector('.globo-selected-items.gf-block-content') ||
      root.querySelector('.globo-selected-items');
  }

  function rowTag(list) {
    return list.tagName === 'UL' || list.tagName === 'OL' ? 'li' : 'div';
  }

  function isLocked(level) {
    return (level === 'make' && !!config.lockMake) ||
      (level === 'model' && !!config.lockModel);
  }

  /* A locked level is the page's own identity, not something the shopper
   * picked, so it gets no chip. Offering to remove the make on a Silverado
   * page would strand them: the dropdown is disabled, so there would be no way
   * to put it back. */
  function levelsOf(state) {
    var out = [];
    for (var i = 0; i < LEVELS.length; i++) {
      if (state[LEVELS[i]] && !isLocked(LEVELS[i])) out.push([LEVELS[i], state[LEVELS[i]]]);
    }
    return out;
  }

  function buildChip(list, level, value) {
    var item = document.createElement(rowTag(list));
    item.className = 'selected-item gf-option-label';
    item.setAttribute('data-underland-chip', level);

    var link = document.createElement('a');
    link.className = 'refine-by-multi-filter';
    link.setAttribute('role', 'button');
    link.setAttribute('tabindex', '0');
    link.setAttribute('aria-label', 'Remove ' + value);
    link.innerHTML =
      '<span class="selected-item"><strong><span class="gf-label"></span></strong></span>' +
      '<span class="gf-clear">' + CLOSE_ICON + '</span>';
    link.querySelector('.gf-label').textContent = value;

    // Bound on the element itself, so it runs before Globo's delegated handlers
    // on document and can stop them from also seeing the click.
    function drop(event) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      dropLevel(level);
    }
    link.addEventListener('click', drop);
    link.addEventListener('keydown', drop);

    item.appendChild(link);
    return item;
  }

  /** Removing a level takes everything under it, the way the dropdowns do. */
  function dropLevel(level) {
    var next = {
      make: desired.make, model: desired.model, year: desired.year, trim: desired.trim
    };
    var from = LEVELS.indexOf(level);
    for (var i = from; i < LEVELS.length; i++) next[LEVELS[i]] = '';

    desired = next;
    converged = false;
    applyLabels(resolveFitments(next.make, next.model, next.year, next.trim));
    render(next);
  }

  function renderChips(state) {
    var list = chipList();
    if (!list) return;

    var wanted = levelsOf(state);

    // Rewriting the list on every pass would feed the observer its own output
    // and loop reconcile. Compare against what is actually in the DOM - not a
    // cached signature - so a rebuild by Globo is still detected and repaired.
    var mine = list.querySelectorAll('[data-underland-chip]');
    if (mine.length === wanted.length) {
      var same = true;
      for (var i = 0; i < wanted.length; i++) {
        var label = mine[i].querySelector('.gf-label');
        if (mine[i].getAttribute('data-underland-chip') !== wanted[i][0] ||
            !label || label.textContent !== wanted[i][1]) {
          same = false;
          break;
        }
      }
      if (same) return;
    }

    for (var r = 0; r < mine.length; r++) mine[r].remove();

    // After Globo's own "Clear all" when it sits inside the list, so ours read
    // as the first filters rather than displacing it. In the sidebar layout it
    // lives in the block title instead, and this finds nothing - which is why
    // the row is walked up to a direct child rather than assumed to be an <li>.
    var before = list.firstChild;
    var anchor = list.querySelector('.clear-refinements');
    if (anchor) {
      var row = anchor;
      while (row && row.parentNode !== list) row = row.parentNode;
      if (row) before = row.nextSibling;
    }

    for (var w = 0; w < wanted.length; w++) {
      list.insertBefore(buildChip(list, wanted[w][0], wanted[w][1]), before);
    }
  }

  /**
   * The vehicle a locked page is about cannot be cleared away.
   *
   * Globo's Clear all wipes every filter including ours, and reading that back
   * would leave a Silverado page showing the whole catalogue with the Make
   * dropdown disabled and empty - no way forward and no way back. Put the lock
   * back instead; only the levels below it clear.
   */
  function restoreLock(state) {
    if (!config.lockMake || state.make === config.lockMake) return state;

    // Putting the vehicle back into `desired` is not enough on its own -
    // reconcile would treat it as already settled and never drive Globo toward
    // it, leaving the dropdowns showing a vehicle the grid is not filtered to.
    converged = false;
    return {
      make: config.lockMake,
      model: config.lockModel || '',
      year: '',
      trim: ''
    };
  }

  /* On a vehicle collection page, pre-selected levels are locked so shoppers
   * drill down within that vehicle instead of switching away from it. */
  function applyLock() {
    if (config.lockMake) {
      var make = dropdown('make');
      if (make) make.disabled = true;
    }
    if (config.lockModel) {
      var model = dropdown('model');
      if (model) model.disabled = true;
    }
  }

  /* --------------------------------------------------------------- Reconcile
   * Globo rebuilds its tree after every filter change, wiping the panel. On
   * each rebuild, drive the tree toward the shopper's selection and mirror back
   * anything they changed through Globo's own UI. */

  function reconcile() {
    if (!hierarchy || !tree()) return;

    // The block's presence is the signal that Globo's vehicle filter exists.
    // Until it does there is nothing for the dropdowns to drive, so the panel
    // stays hidden rather than showing a control that silently does nothing.
    if (!block()) return;
    panel.removeAttribute('hidden');

    inject();

    if (Date.now() <= selfDriveUntil) {
      // Mid-click. Come back once the window closes rather than reading a tree
      // that is still catching up.
      window.clearTimeout(reconcile.retry);
      reconcile.retry = window.setTimeout(reconcile, selfDriveUntil - Date.now() + 50);
      render(desired);
      return;
    }

    var wanted = resolveFitments(desired.make, desired.model, desired.year, desired.trim);
    var applied = checkedLabels();
    var offered = offeredLabels();
    recordOffered(offered);
    var reachable = reachableIn(wanted, offered);

    if (sameValues(reachable, applied)) {
      converged = true;
    } else if (converged) {
      // The selection was applied and no longer is: the shopper cleared it or
      // removed a chip. Mirror the tree rather than re-applying over them.
      // Already converged by definition of this branch - do not re-assert it
      // afterwards, or a lock restored above is immediately clobbered.
      desired = restoreLock(readSelection(applied, desired, offered));
    } else {
      // Still driving toward the selection. Globo narrows a group's values as
      // it re-renders, so labels we could not tick a moment ago may exist now.
      if (wanted.length && reachable.length) {
        applyLabels(wanted);
        render(desired);
        return;
      }

      // Nothing is reachable, so this is as applied as it will get. Set this
      // before restoreLock, which clears it again when it puts a vehicle back.
      converged = true;
      if (!wanted.length) desired = restoreLock(readSelection(applied, desired, offered));
    }

    render(desired);
  }

  function sameValues(a, b) {
    if (a.length !== b.length) return false;
    var x = a.slice().sort();
    var y = b.slice().sort();
    for (var i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
    return true;
  }

  /* ------------------------------------------------------------------ Wiring */

  function onChange(event) {
    var select = event.target;
    var level = select.getAttribute && select.getAttribute('data-fitment-level');
    if (LEVELS.indexOf(level) === -1) return;

    var state = {
      make: value('make'),
      model: value('model'),
      year: value('year'),
      trim: value('trim')
    };

    // Changing a level replaces the selection below it rather than stacking on
    // top of it, so you can never end up on a ZR2 trim of a Tacoma.
    if (level === 'make') { state.model = ''; state.year = ''; state.trim = ''; }
    if (level === 'model') { state.year = ''; state.trim = ''; }
    if (level === 'year') { state.trim = ''; }

    desired = state;
    converged = false;
    applyLabels(resolveFitments(state.make, state.model, state.year, state.trim));
    render(state);
  }

  function value(level) {
    var select = dropdown(level);
    return select ? select.value : '';
  }

  /* The panel is printed elsewhere in the page, then moved to the top of
   * Globo's sidebar - and moved back whenever Globo rebuilds the tree. */
  function inject() {
    var root = tree();
    if (!root || !panel || panel.parentNode === root) return;
    root.insertBefore(panel, root.firstChild);
  }

  function watch() {
    var root = tree();
    if (!root) return;
    if (observer && observedTree === root) return;
    if (observer) observer.disconnect();
    observedTree = root;
    observer = new MutationObserver(function (records) {
      // Our own panel sits inside the observed tree; reacting to its renders
      // would loop reconcile against itself.
      var external = false;
      for (var i = 0; i < records.length; i++) {
        var node = records[i].target;
        if (panel && (node === panel || panel.contains(node))) continue;
        // Our own chips live in Globo's list, outside the panel. Reacting to
        // them would loop reconcile against its own output.
        if (node.closest && node.closest('[data-underland-chip]')) continue;
        external = true;
        break;
      }
      if (!external) return;

      window.clearTimeout(watch.timer);
      watch.timer = window.setTimeout(reconcile, 60);
    });
    // Globo signals checked state by flipping aria-checked (and on some builds
    // a class), which is an attribute change - watching childList alone would
    // miss a shopper clearing a filter through Globo's own UI.
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-checked', 'class']
    });
  }

  function start() {
    panel = document.getElementById('underland-fitment');
    if (!panel || !config.hierarchyUrl) return;

    panel.addEventListener('change', onChange);

    fetch(config.hierarchyUrl)
      .then(function (response) { return response.json(); })
      .then(function (data) {
        hierarchy = data;
        universalValues = data.universalValues ||
          (data.universalValue ? [data.universalValue] : []);

        if (config.lockMake) {
          desired.make = config.lockMake;
          if (config.lockModel) desired.model = config.lockModel;
          applyLabels(resolveFitments(desired.make, desired.model, '', ''));
        }

        reconcile();
        watch();

        // Globo may not have rendered its tree yet on first paint, and it swaps
        // the tree out on each filter change - so keep watching for a
        // replacement rather than binding once and hoping.
        window.setInterval(function () {
          var root = tree();
          if (!root) return;
          if (observedTree !== root) {
            watch();
            reconcile();
          }
        }, 500);
      })
      .catch(function (error) {
        if (window.console) console.error('[underland-fitment]', error);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
